import { useCallback, useRef, useState } from 'react';
import type { TripRoute } from '../types/trip';
import { getVehicleRoute } from '../lib/api/endpoints';
import { tripRouteFromDto } from '../lib/api/mappers';

// FE-27 (2026-09-20) — el máximo que permite el contrato (`maxPoints`, tope
// 20000; por defecto el servidor usa 5000). Cuando el rango excede el tope el
// servidor adelgaza con muestreo UNIFORME (1 de cada n) y pierde los vértices
// de las curvas: medido con el fixture real de Jimi, 1 de 12 deja cuerdas de
// 2 km (el dato crudo no pasa de 205 m). Los presets "Esta semana" y "Semana
// pasada" superan 5000 puntos con facilidad. Con 20000 se adelgaza 4 veces
// menos. Cuesta payload (~2 MB en el peor caso), por eso es un tope y no
// "todo"; el arreglo de fondo es del backend (Douglas–Peucker, ver PENDIENTES).
const HISTORY_MAX_POINTS = 20000;

/**
 * FE-25 — playback histórico: una consulta puntual contra
 * `GET /vehicles/{id}/route?from=…&to=…`, sin suscripción a Firestore.
 *
 * Reutiliza el tipo `TripRoute`/`tripRouteFromDto` de FE-11: el schema
 * `Route` del contrato es el mismo para `/trips/{id}/route` y para
 * `/vehicles/{id}/route`, así que no hace falta un tipo ni un mapper nuevos.
 */
export function useVehicleRouteHistory() {
  const [route, setRoute] = useState<TripRoute | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // 🚨 Sin esto, dos consultas seguidas (el usuario cambia el rango antes de
  // que responda la primera) pueden resolver fuera de orden: la vieja
  // pisaría el resultado de la nueva con datos de otro rango, mismo patrón
  // de `cancelledRef` que ya usa `useVehicles.ts`.
  const requestIdRef = useRef(0);

  const fetchRoute = useCallback(async (vehicleId: string, from: string, to: string) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const dto = await getVehicleRoute(vehicleId, from, to, HISTORY_MAX_POINTS);
      if (requestId !== requestIdRef.current) return;
      setRoute(tripRouteFromDto(dto));
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setRoute(null);
      setError(err instanceof Error ? err : new Error('Error desconocido cargando el histórico de recorrido'));
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    requestIdRef.current++; // invalida cualquier fetchRoute todavía en vuelo
    setRoute(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { route, isLoading, error, fetchRoute, clear };
}
